import React, { useEffect, useState } from 'react';
import { Card, Form, Input, Button, Select, Space, Typography, List, message, Drawer } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import ReportsLayout from './ReportsLayout';
import { testReportQuery } from '../services/aiImageMaskingApi';
import { scopedKey, getActiveAccountId } from '../utils/accountSession';

const { Title, Text } = Typography;

const chartOptionsList = [
  { value: 'line', label: 'Line' },
  { value: 'bar', label: 'Bar' },
  { value: 'pie', label: 'Pie' },
  { value: 'doughnut', label: 'Doughnut' },
  { value: 'radar', label: 'Radar' },
  { value: 'polarArea', label: 'Polar Area' },
  { value: 'bubble', label: 'Bubble' },
  { value: 'scatter', label: 'Scatter' },
];

const ReportsDefine = () => {
  const [queries, setQueries] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editId, setEditId] = useState(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchServerQueries();
  }, []);

  const fetchServerQueries = async (term = '') => {
    setLoading(true);
    try {
      const q = JSON.parse(localStorage.getItem(scopedKey('reports_queries', getActiveAccountId())) || '[]');
      if (Array.isArray(q)) {
        if (term.trim()) {
          const t = term.toLowerCase();
          setQueries(
            q.filter(
              (x) =>
                (x.name || '').toLowerCase().includes(t) ||
                (x.query || '').toLowerCase().includes(t) ||
                (x.chartType || '').toLowerCase().includes(t)
            )
          );
        } else {
          setQueries(q);
        }
      } else {
        setQueries([]);
      }
    } catch (err) {
      setQueries([]);
    } finally {
      setLoading(false);
    }
  };

  const saveQueries = (next) => {
    setQueries(next);
    localStorage.setItem(scopedKey('reports_queries', getActiveAccountId()), JSON.stringify(next));
  };

  const handleSave = (vals) => {
    if (editId) {
      const next = queries.map((q) => (q.id === editId ? { ...q, ...vals } : q));
      saveQueries(next);
      message.success('Report updated');
    } else {
      const next = [...queries, { id: Date.now().toString(), ...vals }];
      saveQueries(next);
      message.success('Report definition added');
    }
    setEditId(null);
    setModalVisible(false);
    form.resetFields();
  };

  const handleDelete = (id) => {
    const next = queries.filter((q) => q.id !== id);
    saveQueries(next);
  };

  return (
    <ReportsLayout>
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <Title level={4}>Define Reports</Title>
        <Text type="secondary">Create or update saved queries that power report cards.</Text>
        <Card
          className="shadow-sm"
          title="Saved Queries"
          extra={
            <Space>
              <Input.Search
                allowClear
                size="small"
                placeholder="Search"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  fetchServerQueries(e.target.value);
                }}
                style={{ width: 220 }}
              />
              <Button
                icon={<PlusOutlined />}
                type="primary"
                onClick={() => {
                  setEditId(null);
                  form.resetFields();
                  setModalVisible(true);
                }}
              >
                New
              </Button>
            </Space>
          }
        >
          {queries.length === 0 ? (
            <Text type="secondary">No queries yet. Click New to add.</Text>
          ) : (
            <List
              loading={loading}
              dataSource={queries}
              renderItem={(item) => (
                <List.Item
                  actions={[
                    <Button
                      key="edit"
                      size="small"
                      onClick={() => {
                        setEditId(item.id);
                        form.setFieldsValue({
                          name: item.name,
                          index: item.index,
                          chartType: item.chartType,
                          query: item.query,
                          chartOptions: item.chartOptions,
                        });
                        setModalVisible(true);
                      }}
                    >
                      Edit
                    </Button>,
                    <Button
                      key="del"
                      danger
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={() => handleDelete(item.id)}
                    >
                      Delete
                    </Button>,
                  ]}
                >
                  <List.Item.Meta
                    title={item.name || 'Untitled'}
                    description={
                      <Space direction="vertical">
                        <Text>Chart: {item.chartType}</Text>
                        <Text type="secondary" style={{ maxWidth: 480, display: 'block' }}>
                          {item.query}
                        </Text>
                      </Space>
                    }
                  />
                </List.Item>
              )}
            />
          )}
        </Card>
      </Space>

      <Drawer
        title={editId ? 'Edit Report' : 'New Report'}
        open={modalVisible}
        placement="right"
        width="50%"
        destroyOnClose
        onClose={() => {
          setModalVisible(false);
          setEditId(null);
        }}
        bodyStyle={{ paddingBottom: 80, resize: 'horizontal', overflow: 'auto' }}
        extra={
          <Space>
            <Button onClick={() => setModalVisible(false)}>Cancel</Button>
            <Button
              type="primary"
              onClick={() => {
                form
                  .validateFields()
                  .then((vals) => handleSave(vals))
                  .catch(() => {});
              }}
            >
              Save
            </Button>
          </Space>
        }
      >
        <Form layout="vertical" form={form} initialValues={{ chartType: 'line' }}>
          <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Name required' }]}>
            <Input placeholder="My latency chart" />
          </Form.Item>
          <Form.Item name="index" label="Index (optional)">
            <Input placeholder="Optional override (defaults to settings index)" />
          </Form.Item>
          <Form.Item name="chartType" label="Chart type" rules={[{ required: true }]}>
            <Select options={chartOptionsList} />
          </Form.Item>
          <Form.Item name="query" label="Query" rules={[{ required: true, message: 'Query required' }]}>
            <Input.TextArea rows={4} placeholder='{"match_all": {}} or DSL' />
          </Form.Item>
          <Form.Item name="chartOptions" label="Chart options (JSON)">
            <Input.TextArea rows={3} placeholder='Optional Chart.js options JSON' />
          </Form.Item>
          <Space>
            <Button
              onClick={async () => {
                try {
                  const vals = form.getFieldsValue();
                  const settings = JSON.parse(localStorage.getItem(scopedKey('reports_settings', getActiveAccountId())) || '{}');
                  const indexVal = vals.index || settings.index;
                  if (!indexVal || !vals.query) {
                    message.error('Index and query are required to test.');
                    return;
                  }
                  let parsedQuery = vals.query;
                  if (typeof parsedQuery === 'string') {
                    parsedQuery = JSON.parse(parsedQuery || '{}');
                  }
                  const res = await testReportQuery({
                    endpoint: settings.endpoint,
                    index: indexVal,
                    apiKey: settings.apiKey,
                    authType: settings.authType || 'apiKey',
                    username: settings.username,
                    password: settings.password,
                    query: parsedQuery,
                  });
                  setTestResult(res);
                  message.success('Query executed.');
                } catch (err) {
                  message.error(err.message || 'Unable to run test');
                }
              }}
            >
              Test query
            </Button>
            {testResult && (
              <Space direction="vertical" size={4}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Hits: {testResult.total} | Took: {testResult.took_ms}ms
                </Text>
                <Card size="small" title="Elasticsearch response" bodyStyle={{ maxHeight: 260, overflow: 'auto' }}>
                  <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', marginBottom: 0 }}>
                    {JSON.stringify(testResult.raw || testResult, null, 2)}
                  </pre>
                </Card>
              </Space>
            )}
          </Space>
        </Form>
      </Drawer>
    </ReportsLayout>
  );
};

export default ReportsDefine;

